/**
 * PurchaseInvoiceService
 * 
 * Handles the complete purchase invoice flow:
 * - Confirmar factura y entrada de mercancía (single transactional action)
 * - Idempotent: safe to call multiple times
 * - Calculates coste_medio, PVP sugerido, registers pending payment
 */
import { tiendaDb } from '../lib/supabase';
import { calcularCosteLinea, calcularPvpSugerido, getMargenAplicable, getReglaRedondeo, calcularPvp, calcularPvpPsicologico, MARGEN_DEFAULT, REGLA_REDONDEO_DEFAULT,  } from '../utils/precioCalculation';

/**
 * Confirm invoice and stock entry — the single transactional action.
 * Uses the DB-level RPC for atomicity and idempotency.
 * Falls back to JS-level processing if RPC is unavailable.
 * 
 * @param {string} facturaId - UUID of the invoice
 * @param {object} options
 * @returns {Promise<{ok, facturaId, lineasProcesadas, lineasStock, lotesCreados, errores, pvpActualizados}>}
 */
export const confirmarFacturaYEntradaMercancia = async (facturaId, options = {}) => {
  if (!facturaId) throw new Error('facturaId es obligatorio');

  // Try DB-level RPC first (atomic, idempotent)
  try {
    const { data, error } = await tiendaDb?.rpc('confirmar_factura_compra', {
      p_factura_id: facturaId,
    });

    if (error) throw error;

    if (data?.ok === false) {
      throw new Error(data?.error || 'Error en la confirmación');
    }

    // After RPC: update PVP on products (RPC handles costs, JS handles PVP logic)
    const pvpResult = await actualizarPvpDespuesDeRecepcion(facturaId);

    return {
      ok: true,
      facturaId,
      idempotent: data?.idempotent || false,
      lineasProcesadas: data?.lineas_procesadas || 0,
      lineasStock: data?.lineas_stock || 0,
      lotesCreados: data?.lotes_creados || 0,
      pvpActualizados: pvpResult?.actualizados || 0,
      errores: data?.errores || [],
    };
  } catch (rpcError) {
    // If RPC fails (function not deployed yet), fall back to JS implementation
    if (rpcError?.message?.includes('function') || rpcError?.message?.includes('does not exist')) {
      return confirmarFacturaYEntradaMercanciaJS(facturaId, options);
    }
    throw rpcError;
  }
};

/**
 * JS fallback for confirmar_factura_compra
 * Implements the same logic as the SQL RPC but in JavaScript
 */
const confirmarFacturaYEntradaMercanciaJS = async (facturaId, options = {}) => {
  const { usuario = 'ERP' } = options;

  // Load invoice with lines and products
  const { data: factura, error: facturaError } = await tiendaDb?.from('facturas_compra')?.select(`
      *,
      lineas:lineas_factura_compra(
        *,
        producto:productos(
          id, nombre, pvp_venta, pvp_sin_iva, pvp_teorico,
          margen_objetivo, bloqueo_precio, actualizar_pvp_automatico,
          recargo_equivalencia_pct, controla_lotes, controla_caducidad,
          categoria:categorias(id, nombre, margen_objetivo, regla_redondeo),
          familia:familias_producto(id, nombre, margen_objetivo, regla_redondeo)
        )
      )
    `)?.eq('id', facturaId)?.single();

  if (facturaError) throw facturaError;

  // Idempotency check
  if (factura?.estado_recepcion === 'RECEPCIONADA') {
    return {
      ok: true,
      idempotent: true,
      facturaId,
      message: 'Factura ya recepcionada anteriormente',
      lineasProcesadas: 0,
      lineasStock: 0,
      lotesCreados: 0,
      pvpActualizados: 0,
      errores: [],
    };
  }

  // Get default almacen
  const { data: almacenes } = await tiendaDb?.from('almacenes')?.select('id, nombre')?.eq('activo', true)?.order('nombre')?.limit(1);
  const almacenId = almacenes?.[0]?.id || null;

  const lineas = factura?.lineas || [];
  const portes = parseFloat(factura?.portes) || 0;
  const baseTotal = lineas?.filter(l => l?.afecta_stock !== false)?.reduce((s, l) => s + (parseFloat(l?.subtotal) || 0), 0);

  let lineasProcesadas = 0;
  let lineasStock = 0;
  let lotesCreados = 0;
  const errores = [];
  const pvpActualizados = new Set();

  for (const linea of lineas) {
    lineasProcesadas++;

    if (linea?.afecta_stock === false || !linea?.producto_id) continue;

    const producto = linea?.producto;
    const cantidad = parseFloat(linea?.cantidad_inventario || linea?.cantidad) || 0;
    if (cantidad <= 0) continue;

    // Idempotency: check if movement already exists
    const { data: existingMov } = await tiendaDb?.from('movimientos_stock')?.select('id')?.eq('referencia_tipo', 'FACTURA_COMPRA')?.eq('referencia_id', facturaId)?.eq('producto_id', linea?.producto_id)?.eq('tipo_movimiento', 'ENTRADA')?.limit(1);

    if (existingMov?.length > 0) continue; // Already processed

    // Calculate costs with portes proration
    const porteProrrateo = baseTotal > 0 && portes > 0
      ? ((parseFloat(linea?.subtotal) || 0) / baseTotal) * portes / cantidad
      : 0;

    const { costeNeto, costeConIva, costeConImpuestos } = calcularCosteLinea({
      precioUnitario: parseFloat(linea?.precio_bruto || linea?.precio_unitario) || 0,
      descuentoImporte: parseFloat(linea?.descuento) || 0,
      descuentoPorcentaje: parseFloat(linea?.descuento_porcentaje) || 0,
      ivaPct: parseFloat(linea?.porcentaje_iva) || 10,
      recargoEquivalenciaPct: parseFloat(producto?.recargo_equivalencia_pct) || 0,
      porteProrrateo,
    });

    // Create lote if needed
    let loteId = null;
    if (producto?.controla_lotes && linea?.numero_lote) {
      try {
        const { data: loteData } = await tiendaDb?.from('lotes')?.insert({
            producto_id: linea?.producto_id,
            numero_lote: linea?.numero_lote,
            lote_proveedor: linea?.numero_lote,
            fecha_entrada: factura?.fecha_factura,
            fecha_caducidad: linea?.fecha_caducidad || null,
            cantidad_inicial: cantidad,
            cantidad_actual: cantidad,
            coste_unitario: costeNeto,
            proveedor_id: factura?.proveedor_id || null,
            factura_compra_id: facturaId,
            linea_factura_compra_id: linea?.id,
            origen: 'COMPRA',
          })?.select('id')?.single();
        if (loteData) {
          loteId = loteData?.id;
          lotesCreados++;
        }
      } catch (e) {
        // Lote may already exist, continue
      }
    }

    // Update inventory with coste_medio
    try {
      await tiendaDb?.rpc('upsert_inventario_entrada', {
        p_producto_id: linea?.producto_id,
        p_almacen_id: almacenId,
        p_cantidad: cantidad,
        p_coste_unitario: costeNeto,
      });
    } catch (invErr) {
      // Fallback: direct update
      try {
        const { data: invExisting } = await tiendaDb?.from('inventario_actual')?.select('id, stock_actual, coste_medio')?.eq('producto_id', linea?.producto_id)?.limit(1);

        if (invExisting?.length > 0) {
          const stockActual = parseFloat(invExisting?.[0]?.stock_actual) || 0;
          const costeMedioActual = parseFloat(invExisting?.[0]?.coste_medio) || 0;
          const nuevoStock = stockActual + cantidad;
          const nuevoCosteMedio = nuevoStock > 0
            ? (stockActual * costeMedioActual + cantidad * costeNeto) / nuevoStock
            : costeNeto;

          await tiendaDb?.from('inventario_actual')?.update({
              stock_actual: nuevoStock,
              coste_medio: nuevoCosteMedio,
              ultimo_coste: costeNeto,
              ultima_actualizacion: new Date()?.toISOString(),
            })?.eq('id', invExisting?.[0]?.id);
        } else {
          await tiendaDb?.from('inventario_actual')?.insert({
            producto_id: linea?.producto_id,
            almacen_id: almacenId,
            stock_actual: cantidad,
            coste_medio: costeNeto,
            ultimo_coste: costeNeto,
            ultima_actualizacion: new Date()?.toISOString(),
          });
        }
      } catch (e) {
        errores?.push(`Inventario ${linea?.descripcion_original}: ${e?.message}`);
      }
    }

    // Create ENTRADA movement
    // coste_unitario = coste_con_impuestos (real cost borne by the store)
    try {
      await tiendaDb?.from('movimientos_stock')?.insert({
        producto_id: linea?.producto_id,
        lote_id: loteId,
        tipo_movimiento: 'ENTRADA',
        cantidad,
        coste_unitario: costeConImpuestos,
        almacen_destino_id: almacenId,
        referencia_tipo: 'FACTURA_COMPRA',
        referencia_id: facturaId,
        observaciones: `Factura ${factura?.numero_factura} - linea:${linea?.id}`,
        usuario,
      });
      lineasStock++;
    } catch (e) {
      // Unique constraint violation = already exists (idempotent)
      if (!e?.message?.includes('unique') && !e?.message?.includes('duplicate')) {
        errores?.push(`Movimiento ${linea?.descripcion_original}: ${e?.message}`);
      } else {
        lineasStock++; // Count as processed
      }
    }

    // Update product costs — all three fields
    try {
      await tiendaDb?.from('productos')?.update({
        precio_compra: costeNeto,
        precio_compra_con_iva: costeConIva,
        coste_con_impuestos: costeConImpuestos,
        iva_compra: parseFloat(linea?.porcentaje_iva) || 10,
        updated_at: new Date()?.toISOString(),
      })?.eq('id', linea?.producto_id);
    } catch {}

    // Save price history
    try {
      await tiendaDb?.from('historico_precios_compra')?.insert({
        producto_id: linea?.producto_id,
        proveedor_id: factura?.proveedor_id || null,
        factura_compra_id: facturaId,
        linea_factura_compra_id: linea?.id,
        fecha: factura?.fecha_factura,
        precio_bruto: parseFloat(linea?.precio_bruto || linea?.precio_unitario) || 0,
        descuento_porcentaje: parseFloat(linea?.descuento_porcentaje) || 0,
        precio_neto: costeNeto,
        iva_porcentaje: parseFloat(linea?.porcentaje_iva) || 10,
        recargo_equivalencia_pct: parseFloat(producto?.recargo_equivalencia_pct) || 0,
        coste_con_impuestos: costeConImpuestos,
        portes_prorrateados: porteProrrateo,
        pvp_sugerido: parseFloat(linea?.pvp_calculado) || 0,
        pvp_actual: parseFloat(producto?.pvp_venta) || 0,
        margen_aplicado: parseFloat(linea?.margen_aplicado) || 0,
        coste_total: costeConImpuestos * cantidad,
      });
    } catch {}

    pvpActualizados?.add(linea?.producto_id);
  }

  // Update PVP on products
  const pvpResult = await actualizarPvpDespuesDeRecepcion(facturaId);

  // Mark invoice as received
  await tiendaDb?.from('facturas_compra')?.update({
    estado_recepcion: 'RECEPCIONADA',
    estado: 'RECEPCIONADA',
    estado_pago: factura?.estado_pago || 'PENDIENTE',
    pendiente_pago: factura?.pendiente_pago ?? factura?.total ?? 0,
    importe_pagado: factura?.importe_pagado || 0,
  })?.eq('id', facturaId);

  return {
    ok: true,
    facturaId,
    idempotent: false,
    lineasProcesadas,
    lineasStock,
    lotesCreados,
    pvpActualizados: pvpResult?.actualizados || pvpActualizados?.size,
    errores,
  };
};

/**
 * Update PVP on products after reception
 * PROTECCIÓN EPELSA: NO sobrescribir pvp_venta — es el PVP maestro procedente de Epelsa.
 * Solo actualizar campos auxiliares: pvp_teorico, pvp_sin_iva, margen_real.
 * pvp_venta solo se actualiza si el producto tiene actualizar_pvp_automatico=true
 * Y bloqueo_precio=false Y no tiene PLU de Epelsa (origen no Epelsa).
 */
const actualizarPvpDespuesDeRecepcion = async (facturaId) => {
  let actualizados = 0;

  try {
    const { data: lineas } = await tiendaDb?.from('lineas_factura_compra')?.select(`
        producto_id, precio_compra_neto, coste_con_impuestos, porcentaje_iva, pvp_calculado, margen_aplicado,
        producto:productos(
          id, pvp_venta, plu, bloqueo_precio, actualizar_pvp_automatico,
          margen_objetivo, recargo_equivalencia_pct,
          categoria:categorias(id, margen_objetivo, regla_redondeo),
          familia:familias_producto(id, margen_objetivo, regla_redondeo)
        )
      `)?.eq('factura_id', facturaId)?.not('producto_id', 'is', null);

    const processed = new Set();

    for (const linea of lineas || []) {
      if (!linea?.producto_id || processed?.has(linea?.producto_id)) continue;
      processed?.add(linea?.producto_id);

      const producto = linea?.producto;
      if (!producto) continue;

      // Skip if price is locked
      if (producto?.bloqueo_precio === true) continue;

      // PVP and margin calculated on coste_con_impuestos (real cost)
      const costeBase = parseFloat(linea?.coste_con_impuestos) || parseFloat(linea?.precio_compra_neto) || 0;
      if (costeBase <= 0) continue;

      const ivaPct = parseFloat(linea?.porcentaje_iva) || 10;
      const pvpInfo = calcularPvpSugerido(producto, costeBase, ivaPct);

      try {
        // PROTECCIÓN EPELSA: Si el producto tiene PLU (artículo Epelsa),
        // NO sobrescribir pvp_venta. Solo actualizar campos auxiliares.
        const tieneEpelsa = !!(producto?.plu);
        const puedeActualizarPvpVenta = !tieneEpelsa && producto?.actualizar_pvp_automatico !== false;

        const updatePayload = {
          pvp_teorico: pvpInfo?.pvpTeorico,
          pvp_sin_iva: pvpInfo?.pvpSinIva,
          margen_real: Math.round(pvpInfo?.margenReal * 10000) / 100,
          updated_at: new Date()?.toISOString(),
        };

        // Solo actualizar pvp_venta si el producto NO es de Epelsa y tiene auto-update activo
        if (puedeActualizarPvpVenta) {
          updatePayload.pvp_venta = pvpInfo?.pvpSugerido;
        }
        // Si es producto Epelsa (tiene PLU), pvp_venta NO se toca.
        // El PVP recomendado queda en pvp_teorico como campo auxiliar.

        await tiendaDb?.from('productos')?.update(updatePayload)?.eq('id', linea?.producto_id);
        actualizados++;
      } catch {}
    }
  } catch {}

  return { actualizados };
};

/**
 * Get PVP preview for a line before saving
 */
export const calcularPvpPreviewLinea = (linea, producto, margenDefault = MARGEN_DEFAULT, reglaDefault = REGLA_REDONDEO_DEFAULT) => {
  const costeNeto = parseFloat(linea?.precio_compra_neto || linea?.coste_neto || linea?.precio_unitario) || 0;
  const ivaPct = parseFloat(linea?.porcentaje_iva) || 10;

  if (costeNeto <= 0) return null;

  const margen = producto ? getMargenAplicable(producto, margenDefault) : margenDefault;
  const regla = producto ? getReglaRedondeo(producto, reglaDefault) : reglaDefault;
  const { pvpSinIva, pvpConIva, margenReal } = calcularPvp(costeNeto, margen, ivaPct);
  const pvpSugerido = calcularPvpPsicologico(pvpConIva, regla);

  return {
    costeNeto,
    pvpSinIva,
    pvpTeorico: pvpConIva,
    pvpSugerido,
    margenReal,
    margenAplicado: margen,
    pvpActual: producto?.pvp_venta || 0,
    bloqueado: producto?.bloqueo_precio === true,
  };
};

export default {
  confirmarFacturaYEntradaMercancia,
  calcularPvpPreviewLinea,
};
